import { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

export interface InfoContent {
  title: string;
  text: string;
  foot?: string;
}

interface Props {
  info: InfoContent;
}

const POP_WIDTH = 320;

export default function InfoButton({ info }: Props) {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0, arrowLeft: 16 });

  const show = useCallback(() => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const left = Math.min(window.innerWidth - POP_WIDTH - 16, Math.max(8, r.left - 8));
    const top = r.bottom + window.scrollY + 8;
    const arrowLeft = Math.max(10, r.left - left + r.width / 2 - 5);
    setPos({ left, top, arrowLeft });
    setOpen(true);
  }, []);

  const hide = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => hide();
    const onClickAway = () => hide();
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("click", onClickAway);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClickAway);
    };
  }, [open, hide]);

  return (
    <>
      <button
        ref={ref}
        type="button"
        className="info"
        aria-label={`Explain: ${info.title}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.stopPropagation();
          if (open) hide(); else show();
        }}
      >
        i
      </button>
      {open &&
        createPortal(
          <div
            className="info-pop"
            role="tooltip"
            style={{ left: pos.left, top: pos.top }}
          >
            <div className="info-pop-arrow" style={{ left: pos.arrowLeft }} />
            <div className="info-pop-title">{info.title}</div>
            <div className="info-pop-text">{info.text}</div>
            {info.foot && <div className="info-pop-foot">{info.foot}</div>}
          </div>,
          document.body
        )}
    </>
  );
}
